// "Start from a sample packet": upload one Sample_Packet and an optional prompt, start a Packet_Suggest_Run, and go to
// its proposal page (blueprint-sets Req 8.1, 9.2, 9.5; design "components/PacketUpload.tsx"). Reuses the
// Start_From_Document_Flow pieces: FileUpload, useFileCheck, the prompt Textarea, and startDocumentRun.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Textarea from '@cloudscape-design/components/textarea';
import { MAX_SUGGEST_PROMPT_CHARS, tooManyPacketPagesMessage } from '@bda/shared';
import { runtimeConfig } from '../../config';
import { ErrorDetail } from '../../lib/notify';
import { useFileCheck } from '../../lib/pageCount';
import { startDocumentRun } from '../runs/runFlow';
import { PROMPT_HINT, PROMPT_LABEL } from '../blueprints/SuggestModal';

const fmt = (n: number): string => n.toLocaleString('en-US');

export const PACKET_PROCESSING_TEXT = 'BDA processes every page; a long packet can take several minutes.';
export const PACKET_PAGES_UNKNOWN_TEXT = `Page count unknown. ${PACKET_PROCESSING_TEXT}`;

/** Text shown before the Packet_Suggest_Run starts (Req 9.5). */
export const packetPagesText = (pages: number | null): string =>
  pages === null
    ? PACKET_PAGES_UNKNOWN_TEXT
    : `${fmt(pages)} ${pages === 1 ? 'page' : 'pages'}. ${PACKET_PROCESSING_TEXT}`;

/** File types and limits of a Sample_Packet: the run limits with `sets.suggestMaxPages` pages (Req 8.1). */
export const packetLimitsText = (maxPages: number): string =>
  `PDF, PNG, JPEG, or TIFF up to 50 MB and ${fmt(maxPages)} pages.`;

export interface PacketUploadProps {
  /** Prefix of element ids (prompt and page text), so two instances on one page do not collide. Default `packet-upload`. */
  idPrefix?: string;
  /** Called with the new Packet_Suggest_Run's id instead of navigating to `/sets/proposals/<runId>`. */
  onStarted?: (runId: string) => void;
}

export function PacketUpload({ idPrefix = 'packet-upload', onStarted }: PacketUploadProps) {
  const navigate = useNavigate();
  const maxPages = runtimeConfig().sets.suggestMaxPages;
  const [files, setFiles] = useState<File[]>([]);
  const [prompt, setPrompt] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const file = files[0];
  // Req 9.2: a packet over `suggestSet.maxPages` is rejected here, with the API's message, before any upload.
  const check = useFileCheck(file, { maxPages, message: tooManyPacketPagesMessage });
  const promptTooLong = prompt.trim().length > MAX_SUGGEST_PROMPT_CHARS;
  const working = progress !== null;

  const disabledReason = !file
    ? 'Choose a sample packet first.'
    : check.error
      ? 'Choose a supported sample packet first.'
      : promptTooLong
        ? `Shorten the prompt to ${fmt(MAX_SUGGEST_PROMPT_CHARS)} characters.`
        : check.checking
          ? 'Checking the document.'
          : undefined;

  const start = async () => {
    if (!file || disabledReason) return;
    setError(null);
    setProgress(0);
    try {
      const trimmed = prompt.trim();
      const runId = await startDocumentRun({
        file,
        mode: 'packetSuggest',
        prompt: trimmed || undefined,
        onProgress: setProgress,
      });
      if (onStarted) onStarted(runId);
      else navigate(`/sets/proposals/${runId}`);
    } catch (e) {
      setError(e);
      setProgress(null);
    }
  };

  const showPages = file && !check.error && !check.checking;

  return (
    <SpaceBetween size="m">
      {error !== null && (
        <Alert type="error" header="Could not start the proposal">
          <ErrorDetail error={error} action="Suggest from packet" />
        </Alert>
      )}
      <FormField label="Sample packet" description={packetLimitsText(maxPages)} errorText={check.error}>
        <FileUpload
          value={files}
          onChange={(e) => {
            setFiles(e.detail.value);
            setError(null);
          }}
          accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
          i18nStrings={{
            uploadButtonText: () => 'Choose file',
            dropzoneText: () => 'Drop a file to upload',
            removeFileAriaLabel: () => 'Remove file',
            limitShowFewer: 'Show fewer',
            limitShowMore: 'Show more',
            errorIconAriaLabel: 'Error',
            warningIconAriaLabel: 'Warning',
          }}
          showFileSize
        />
      </FormField>
      {showPages && (
        <Box color="text-body-secondary">
          <span id={`${idPrefix}-pages`}>{packetPagesText(check.pages)}</span>
        </Box>
      )}
      <FormField
        label={
          <span>
            {PROMPT_LABEL} <i>- optional</i>
          </span>
        }
        description={PROMPT_HINT}
        constraintText={`${fmt(prompt.trim().length)}/${fmt(MAX_SUGGEST_PROMPT_CHARS)} characters`}
        errorText={promptTooLong ? `Keep the prompt to ${fmt(MAX_SUGGEST_PROMPT_CHARS)} characters.` : undefined}
      >
        <Textarea
          value={prompt}
          rows={3}
          disabled={working}
          controlId={`${idPrefix}-prompt`}
          onChange={(e) => setPrompt(e.detail.value)}
        />
      </FormField>
      {progress !== null && progress < 1 && <ProgressBar value={Math.round(progress * 100)} label="Uploading" />}
      {progress !== null && progress >= 1 && <StatusIndicator type="loading">Starting the proposal.</StatusIndicator>}
      <Box>
        <Button
          variant="primary"
          disabled={disabledReason !== undefined}
          disabledReason={disabledReason}
          loading={working}
          ariaDescribedby={showPages ? `${idPrefix}-pages` : undefined}
          onClick={() => void start()}
        >
          Propose a set
        </Button>
      </Box>
    </SpaceBetween>
  );
}
